/**
 * ViewContext - Centralized view/navigation state management
 * Phase 2 Refactoring: Stop view resets / blank screens forever
 * 
 * Handles:
 * - currentView / mobileView state
 * - navigateTo(view) function
 * - "auth required before chat" gating
 * - "post-auth always land on dashboard" rule
 * 
 * App.js consumes: currentView, mobileView, navigateTo, showAuthRequired
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';

const ViewContext = createContext(null);

export const useView = () => {
  const context = useContext(ViewContext);
  return context;
};

export const ViewProvider = ({ children }) => {
  // Get auth state from AuthContext
  const auth = useAuth();
  const isAuthenticated = auth?.isAuthenticated || false;
  const justAuthenticated = auth?.justAuthenticated || false;
  const authUser = auth?.user || null;
  
  // Check for test mode
  const testMode = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('test') === 'true';
  }, []);
  
  // Mobile layout detection — robust against extension/iframe environments
  // where window.outerWidth can be 0 and innerWidth can be unreliable on
  // initial render. We use documentElement.clientWidth as the primary
  // measurement (always reflects the rendered layout viewport) and fall
  // back to innerWidth, then a desktop default of 1920 to avoid false
  // mobile detection on edge environments (e.g. Chrome extension contexts
  // where outerWidth = 0 was wrongly flipping desktop into mobile layout).
  const detectMobile = () => {
    if (typeof window === 'undefined') return false;
    // NEVER use outerWidth — it returns 0 in extension/iframe environments
    const width = document.documentElement?.clientWidth || window.innerWidth || 1920;
    const isMobileViewport = width <= 768;
    const isMobileUserAgent = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    // Trust the user agent for true mobile devices; otherwise require a
    // genuine narrow viewport (width > 0 guards against the 0/undefined
    // case which would otherwise resolve as a 0-width "mobile").
    return isMobileUserAgent || (isMobileViewport && width > 0);
  };

  const [useMobileLayout, setUseMobileLayout] = useState(() => detectMobile());
  
  // Guest birth data from session storage
  const getGuestBirthData = useCallback(() => {
    try {
      const data = sessionStorage.getItem('guestBirthData');
      if (!data) return null;
      return JSON.parse(data);
    } catch {
      return null;
    }
  }, []);
  
  const [guestBirthData, setGuestBirthData] = useState(getGuestBirthData);
  
  // Computed: has birth data from any source
  const hasBirthData = useMemo(() => {
    return !!(
      (authUser?.date && authUser?.time && authUser?.place) ||
      (guestBirthData?.date && guestBirthData?.time && guestBirthData?.place)
    );
  }, [authUser, guestBirthData]);
  
  // Computed: can access chat (requires Google auth or test mode)
  const canAccessChat = useMemo(() => {
    const hasGoogleAuth = isAuthenticated || !!authUser;
    return hasGoogleAuth || testMode;
  }, [isAuthenticated, authUser, testMode]);
  
  // Determine initial view - CRITICAL: post-auth always lands on dashboard
  const getInitialView = useCallback(() => {
    console.log('[VIEW] getInitialView:', { justAuthenticated, hasBirthData, testMode, isAuthenticated });
    
    // TRIAL REDIRECT: /chitragupt path → go directly to chat
    if (window.location.pathname === '/chitragupt') {
      console.log('[VIEW] getInitialView: /chitragupt path — routing to chat');
      return 'chat';
    }

    // Test mode goes to dashboard
    if (testMode) return 'conversational';
    
    // RULE: Post-auth always land on dashboard (prevents blank screen)
    if (justAuthenticated && hasBirthData) {
      console.log('[VIEW] Post-auth: landing on dashboard');
      return 'conversational';
    }
    
    // Authenticated with birth data
    if (isAuthenticated && authUser?.onboarding_complete && hasBirthData) {
      return 'conversational';
    }
    
    // Has guest birth data (completed onboarding as guest)
    if (hasBirthData) return 'conversational';
    
    // Default to onboarding
    return 'onboarding';
  }, [justAuthenticated, hasBirthData, testMode, isAuthenticated, authUser?.onboarding_complete]);
  
  // View state
  const [currentView, setCurrentView] = useState(getInitialView);
  const [mobileView, setMobileView] = useState('dashboard');
  const [showAuthRequired, setShowAuthRequired] = useState(false);
  
  // Handle window resize for mobile layout
  useEffect(() => {
    const handleResize = () => {
      // Mirror detectMobile() logic — same robust width resolution + UA check
      const width = document.documentElement?.clientWidth || window.innerWidth || 1920;
      const isMobileUA = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setUseMobileLayout(isMobileUA || (width > 0 && width <= 768));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // RULE: Post-auth always land on dashboard (sync with auth state)
  useEffect(() => {
    if (justAuthenticated && hasBirthData) {
      console.log('[VIEW] Auth completed - ensuring dashboard view');
      // Clear any chat resume flags
      sessionStorage.removeItem('resumeChatAfterAuth');
      localStorage.removeItem('pending_return_view');
      localStorage.removeItem('pending_chat_message');
      
      // Only reset if coming from onboarding
      if (currentView === 'onboarding') {
        setCurrentView('conversational');
        setMobileView('dashboard');
      }
    }
  }, [justAuthenticated, hasBirthData, currentView]);
  
  /**
   * Navigate to a view with auth gating for chat
   * RULE: Chat requires authentication
   */
  const navigateTo = useCallback((destination) => {
    console.log('[VIEW] navigateTo:', destination, { canAccessChat, currentView });
    
    // Chat requires authentication
    if (destination === 'chat' && !canAccessChat) {
      console.log('[VIEW] Chat requires auth - showing AuthRequiredScreen');
      setShowAuthRequired(true);
      return false;
    }
    
    setCurrentView(destination);
    
    // Sync mobile view
    if (destination === 'chat') {
      setMobileView('chat');
    } else if (destination === 'conversational') {
      setMobileView('dashboard');
    }
    
    return true;
  }, [canAccessChat, currentView]);
  
  /**
   * Handle mobile tab change with auth gating
   */
  const handleMobileTabChange = useCallback((tabId) => {
    console.log('[VIEW] handleMobileTabChange:', tabId);
    
    if (tabId === 'chat' && !canAccessChat) {
      setShowAuthRequired(true);
      return;
    }
    
    setMobileView(tabId);
    
    if (tabId === 'chat') {
      setCurrentView('chat');
    } else if (tabId === 'dashboard' && currentView === 'chat') {
      setCurrentView('conversational');
    }
  }, [canAccessChat, currentView]);
  
  /**
   * Store guest birth data
   */
  const saveGuestBirthData = useCallback((data) => {
    sessionStorage.setItem('guestBirthData', JSON.stringify(data));
    setGuestBirthData(data);
    console.log('[VIEW] Guest birth data saved');
  }, []);
  
  /**
   * Clear guest birth data
   */
  const clearGuestBirthData = useCallback(() => {
    sessionStorage.removeItem('guestBirthData');
    setGuestBirthData(null);
    console.log('[VIEW] Guest birth data cleared');
  }, []);

  const value = {
    // State
    currentView,
    mobileView,
    useMobileLayout,
    showAuthRequired,
    testMode,
    guestBirthData,
    hasBirthData,
    canAccessChat,
    
    // Setters (for backward compatibility during migration)
    setCurrentView,
    setMobileView,
    setShowAuthRequired,
    
    // Actions
    navigateTo,
    handleMobileTabChange,
    saveGuestBirthData,
    clearGuestBirthData,
  };

  return (
    <ViewContext.Provider value={value}>
      {children}
    </ViewContext.Provider>
  );
};

export default ViewContext;
